import type { z } from 'zod';
import type {
  JsonObject,
  ProtocolSchemaDescriptor,
} from '../contracts/index.ts';
import { zodToJsonSchema } from 'zod-to-json-schema';
import {
  protocolSchemaDescriptorSchema,
} from '../contracts/index.ts';
import { RegistryCore } from './RegistryCore.ts';

export interface ProtocolSchemaDefinition {
  key: string;
  title: string;
  description?: string;
  schemaVersion: string;
  runtimeSchema: z.ZodType<unknown>;
  semanticConstraints?: string[];
  aiGuidance?: string[];
}

interface ProtocolSchemaRegistryEntry extends ProtocolSchemaDefinition {
  descriptor: ProtocolSchemaDescriptor;
}

export class ProtocolSchemaRegistry {
  private readonly entries = new RegistryCore<ProtocolSchemaRegistryEntry>();

  register(definition: ProtocolSchemaDefinition): this {
    const descriptor = protocolSchemaDescriptorSchema.parse({
      key: definition.key,
      title: definition.title,
      description: definition.description ?? '',
      schemaVersion: definition.schemaVersion,
      jsonSchema: serializeZodSchema(definition.runtimeSchema, definition.key),
      semanticConstraints: definition.semanticConstraints ?? [],
      aiGuidance: definition.aiGuidance ?? [],
    });
    this.entries.register({
      ...definition,
      descriptor,
    });
    return this;
  }

  registerMany(definitions: ProtocolSchemaDefinition[]): this {
    const prepared = definitions.map((definition) => {
      const temporary = new ProtocolSchemaRegistry().register(definition);
      return temporary.entries.resolve(definition.key);
    });
    const keys = new Set<string>();
    for (const entry of prepared) {
      if (keys.has(entry.key) || this.entries.has(entry.key))
        throw new Error(`协议 Schema 已经注册：${entry.key}`);
      keys.add(entry.key);
    }
    for (const entry of prepared)
      this.entries.register(entry);
    return this;
  }

  has(key: string): boolean {
    return this.entries.has(key);
  }

  getDescriptor(key: string): ProtocolSchemaDescriptor {
    return protocolSchemaDescriptorSchema.parse(
      structuredClone(this.entries.resolve(key).descriptor),
    );
  }

  getRuntimeSchema(key: string): z.ZodType<unknown> {
    return this.entries.resolve(key).runtimeSchema;
  }

  validate(key: string, value: unknown): ReturnType<
    z.ZodType<unknown>['safeParse']
  > {
    return this.getRuntimeSchema(key).safeParse(value);
  }

  listDescriptors(): ProtocolSchemaDescriptor[] {
    return this.entries.list().map(entry =>
      this.getDescriptor(entry.key),
    );
  }

  query(text?: string): ProtocolSchemaDescriptor[] {
    const normalized = text?.trim().toLocaleLowerCase();
    if (!normalized)
      return this.listDescriptors();
    return this.listDescriptors().filter((descriptor) => {
      const searchable = [
        descriptor.key,
        descriptor.title,
        descriptor.description,
        ...descriptor.semanticConstraints,
        ...descriptor.aiGuidance,
      ].join(' ').toLocaleLowerCase();
      return searchable.includes(normalized);
    });
  }
}

export function serializeZodSchema(
  schema: z.ZodType<unknown>,
  name: string,
): JsonObject {
  return zodToJsonSchema(schema, {
    name,
    $refStrategy: 'root',
    definitionPath: '$defs',
    target: 'jsonSchema7',
    effectStrategy: 'input',
  }) as JsonObject;
}

/**
 * Serialize a schema for embedding inside another JSON Schema document.
 *
 * Named schemas intentionally use a root `$ref`, which is useful for protocol
 * documents but becomes invalid when the schema is nested under a component's
 * `config` property. Component work orders therefore need an inline schema.
 */
export function serializeInlineZodSchema(
  schema: z.ZodType<unknown>,
): JsonObject {
  return zodToJsonSchema(schema, {
    $refStrategy: 'none',
    target: 'jsonSchema7',
    effectStrategy: 'input',
  }) as JsonObject;
}
